<template>
  <div class="htli-responsive">
    <!-- VIEW ALL - MAILBOX/PROJECT HOME LINK -->
    <div v-if="fromAll">
      <div class="htli-responsive__item-row-parent">
        <router-link :to="getHomeRoute(item)" class="just-a-link">
          {{ item.parent.name }}
        </router-link>
      </div>
    </div>

    <!-- CONVERSATION: Customer -->
    <div v-if="isConversation" class="htli-responsive__convo-row-1">
      <div class="htli-responsive__convo-row-1__customer-name">
        <avatar :person="item.customer" :size="16" />
        <span>{{ item.customer.name }}</span>
      </div>
    </div>

    <!-- Ref number and subject -->
    <div class="htli-responsive__title-details">
      <div class="htli-responsive__title-details__subject-info">
        #{{ item.refNumber }}:&nbsp;
        <span
          :class="{'htli-responsive__title-details__subject-info__subject-new':item.hasUpdates}"
          class="htli-responsive__title-details__subject-info__subject"
        >{{ item.title }}</span>
      </div>
    </div>

    <!-- Item description (single line) -->
    <div v-if="hasDesc(item)" class="htli-responsive__item_description">
      <span :title="item.text">{{item.text}}</span>
    </div>

    <div v-if="item.tags && item.tags.length > 0" class="task-tags-wrapper">
      <div class="task-tags-container">
        <span
          v-for="tag in item.tags"
          :key="tag"
          class="task-tag"
        >
          {{ tag }}
        </span>
      </div>
    </div>

    <!-- Custom field values flagged to show on listings -->
    <custom-field-value-pills
      v-if="customFields.length > 0"
      :fields="customFields"
      :values="item.listCustomFieldValues"
      class="htli-responsive__custom-fields"
    />


    <!-- PRIORITY | STATUS | DUE DATE | ASSIGNEE -->
    <div class="htli-responsive__footer">
      <div class="htli-responsive__footer__chicklets">
        <avatar :size="24" :person="item.assignedTo" show-name-on-hover />
        <priority-chicklet :priority="item.priority" :hide-icon="$screen.isMobilePortrait($mq)" />
        <status-chicklet :status="item.status" :max-width="statusChickletWidth" />
        <due-date-chicklet :due-date="item.dueDate" />
        <div v-if="hasCounts(item)" class="htli-responsive__footer__chicklets__counts">
          <span v-if="item.linkedTasksCount > 0" title="Linked tasks">
            <i class="ddi ddi-task" />
            <span>{{ item.linkedTasksCount }}</span>
          </span>
          <span v-if="item.linkedConversationsCount > 0" title="Linked conversations">
            <i class="ddi ddi-conversation" /> 
            <span>{{ item.linkedConversationsCount }}</span>
          </span>
          <span v-if="item.threadCount > 0" title="Thread count">
            <i class="ddi ddi-chat-balloon" /> 
            <span>{{ item.threadCount }}</span>
          </span>
          <span v-if="item.totalChecklistItemsCount > item.completedChecklistItemsCount" title="Checklist count">
            <i class="ddi ddi-everything" /> 
            <span>({{ item.completedChecklistItemsCount }}/{{ item.totalChecklistItemsCount }})</span>
          </span>
        </div>
      </div>
      <div class="htli-responsive__footer__last-updated">
        {{ item.lastUpdated }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* Mixins */
import { defineComponent } from 'vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data Types */
import { EEntities } from '@/types'
import { IHomeTableItem } from '@/types/data-objects/items'
import { ICustomField } from '@/api/types/response.types'

export default defineComponent({
  name: 'HomeTableListItemResponsive',

  props: {
    item: {
      type: Object as () => IHomeTableItem,
      required: true,
    },
    fromAll: {
      type: Boolean,
      default: true,
    },
    customFields: {
      type: Array as () => ICustomField[],
      default: (): ICustomField[] => [],
    },
  },

  data() {
    return {
      RouteAssistant,
    }
  },

  computed: {
    statusChickletWidth(): string {
      return this.$screen.isMobile(this.$mq) ? '120px' : ''
    },
    isConversation(): boolean {
      if (this.item.customer) {
        return true
      }
      return false
    },
    isTask(): boolean {
      return !this.isConversation
    },
  },
  methods: {
    getHomeRoute(item: IHomeTableItem) {
      switch (item.entityType) {
        case EEntities.MAILBOXES:
          return RouteAssistant.getMailboxLandingRoute(
            this.$route.params.accountId,
            item.parent.id,
            item.parent.name,
          )
        case EEntities.PROJECTS:
          return RouteAssistant.getProjectLandingRoute(
            this.$route.params.accountId,
            item.parent.id,
            this.$permissions,
            item.parent.name,
          )
      }
    },
    hasDesc(item: IHomeTableItem): boolean {
      return item.text && item.text.length > 0
    },
    hasCounts(item: IHomeTableItem): boolean {
      return (
        this.hasLinkedTasks(item) ||
        this.hasLinkedConversations(item) ||
        this.hasThreadCount(item) ||
        this.hasChecklistCount(item)
      )
    },
    hasLinkedTasks(item: IHomeTableItem): boolean {
      return item.linkedTasksCount > 0
    },
    hasLinkedConversations(item: IHomeTableItem): boolean {
      return item.linkedConversationsCount && item.linkedConversationsCount > 0
    },
    hasThreadCount(item: IHomeTableItem): boolean {
      return item.threadCount > 0
    },
    hasChecklistCount(item: IHomeTableItem): boolean {
      return item.totalChecklistItemsCount > item.completedChecklistItemsCount
    },
  },
})
</script>

<style lang="scss">
.htli-responsive {
  width: 100%;

  .task-tags-wrapper {
    margin: 4px 0 8px 0;
    display: block !important;
    width: 100%;
    text-align: left !important;
  }

  .task-tags-container {
    display: block;
    text-align: left;
  }

  .task-tag {
    display: inline-block;
    padding: 4px 6px;
    margin-right: 4px;
    margin-bottom: 2px;
    font-size: 12px;
    white-space: nowrap;
    line-height: 1.2;
    vertical-align: top;
    background: var(--color-background-secondary);
    color: var(--color-text-secondary);
    border-radius: 20px;
    border-color: var(--color-text-third);
    border-width: 1px;
    border-style: solid;
  }

  .task-tag:last-child {
    margin-right: 0;
  }

  &__custom-fields {
    margin: 4px 0 6px 0;
  }

  /* Project/mailbox home button for View All */
  &__item-row-parent {
    a {
      font-size: 13px;
    }
  }

  &__convo-row-1 {
    display: flex;
    justify-content: space-between;

    &__customer-name {
      margin: 2px 8px 0 0;
      @include single-line-ellipsis;

      .avatar {
        margin-right: 4px;
      }

      > span {
        @include lettering--p-sm;
      }
    }
  }

  &__title-details {
    display: flex;
    justify-content: space-between;
    @include lettering--p;

    &__subject-info {
      padding-top: 4px;
      padding-bottom: 1px;
      color: var(--color-text-secondary);

      &__subject-new {
        font-weight: $bold;
      }
    }
  }

  &__item_description {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--color-text-third);
    font-size: 13px;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 6px;

    &__chicklets {
      display: flex;
      flex-wrap: wrap;
      align-items: center;

      > * {
        margin-right: 6px;
      }

      &__counts {
        font-size: 13px;
        color: var(--color-text-third);

        span {
          padding-right: 2px;
        }
      }
    }

    &__last-updated {
      font-size: 13px;
      color: var(--color-text-third);
      white-space: nowrap;
    }
  }
}
</style>
